import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { reportRepository } from "$root/functional-areas/reports/repository";
import { storyfolioRepository } from "$root/functional-areas/storyfolio/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { useUserValue } from "$root/functional-areas/user";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { formatDate } from "$root/localization/formatters";
import { PortfolioStudioTab } from "$root/pages/PortfoliosStudio/portfolio-studio-tabs";
import { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import { ellipsis, typedObjectValues } from "$root/utils";

import { HmmRegion, type MarketViewType } from "@eu.mdotm.api/sphere";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import type { Crumb, NestedCrumb } from "@mdotm/shadcn/mdotm/components";
import { findPathInTree } from "@mdotm/utils/collections";
import type { AsyncState } from "@mdotm/utils/promise";
import { useTranslation } from "react-i18next";
import type { AppDrawerModuleItem } from "../AppDrawer";
import { useAdminPanelItems } from "../AppDrawer/AdminPanelModule";
import type { AppDrawerTreeNode } from "../AppDrawer/HierarchicalTreeModule";
import { useMarketInsightItems } from "../AppDrawer/MarketInsightModule";
import { usePortfolioModuleWithCommonItems } from "../AppDrawer/PortfolioStudioModule";
import { useSettingsItems } from "../AppDrawer/SettingsModule";
import { useStoryfolioItems } from "../AppDrawer/StofyfolioModule";
import { hasAccess } from "../AuthorizationGuard";
import { iconAndColorByInvestmentStatus } from "../LabelRounded/Index";
import { typedUrlForRoute } from "../PlatformRouter/RoutesDef";
import ColoredRectangle from "../icons/ColoredRectangle";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useCrumbs = () => {
	const user = useUserValue();
	const { t } = useTranslation();

	const portfolioStudio = usePortfolioModuleWithCommonItems();
	const marketInsight = useMarketInsightItems();
	const storyfolio = useStoryfolioItems();
	const settings = useSettingsItems();
	const admin = useAdminPanelItems();

	return {
		//#region Portfolio
		portfolioStudio: fromAppDrawerModules(
			{
				href: hasAccess(user, { requiredService: "SMART_WORKSPACE" })
					? typedUrlForRoute("PortfolioStudio/DefaultWorkspace", {})
					: typedUrlForRoute("PortfoliosStudio", {}),
				icon: "portfolio-studio" satisfies IconName,
			},
			portfolioStudio,
			{
				MarketViews: () =>
					marketViewRepository.list.fetchQuery().then((list) =>
						list
							.slice()
							.sort(builtInSortFnFor("creationDate", "desc"))
							.map((marketView) => ({
								id: marketView.uuid,
								label: marketView.name ?? "",
								href: typedUrlForRoute("MarketViewWorkSpace/View", {
									uuid: marketView.uuid!,
								}),
							})),
					),
				Portfolios: hasAccess(user, { requiredService: "SMART_WORKSPACE" })
					? () =>
							workspaceRepository.tree.fetchQuery().then((workspacesTrunk) =>
								workspacesTrunk.children
									.filter((child) => child.name !== "Portfolios")
									.map((child) => ({
										id: child.smartWorkspaceUuid,
										label: child.name,
										href:
											child.smartWorkspaceUuid &&
											typedUrlForRoute("PortfolioStudio/Workspace", { id: child.smartWorkspaceUuid }),
									})),
							)
					: () =>
							workspaceRepository.getAllPortfolios.fetchQuery().then(({ items: list }) =>
								(list ?? []).flatMap((item): [NestedCrumb] | [] => {
									const investmentMinInfo = item.predefinedFields;
									if (!investmentMinInfo) {
										return [];
									}
									const isManualCreation =
										investmentMinInfo.status === "DRAFT" && investmentMinInfo.lastAction === "UPLOAD";
									const isPipelineCreation =
										investmentMinInfo.status === "DRAFT" && investmentMinInfo.lastAction === "CREATION";
									return [
										{
											id: investmentMinInfo.uuid!,
											label: `${investmentMinInfo.name}${investmentMinInfo.status === "DRAFT" ? " (draft)" : ""}`,
											href: isManualCreation
												? typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid: investmentMinInfo.uuid })
												: isPipelineCreation
													? typedUrlForRoute("Portfolios/Draft", { portfolioUid: investmentMinInfo.uuid! })
													: investmentMinInfo.status === "REVIEW"
														? typedUrlForRoute("ReviewInstruments", {
																uuid: investmentMinInfo.uuid!,
																entity: ReviewEntity.INVESTMENT,
															})
														: typedUrlForRoute("PortfolioDetails", { portfolioUid: investmentMinInfo.uuid! }),
											icon: (
												<ColoredRectangle
													variant="vertical"
													color={
														investmentMinInfo?.status
															? iconAndColorByInvestmentStatus[investmentMinInfo?.status].color
															: "transparent"
													}
												/>
											),
										},
									];
								}),
							),
				References: () =>
					Promise.all([
						referenceRepository.referenceList.fetchQuery(),
						referenceRepository.benchmarkList.fetchQuery(),
					]).then(([references, benchmarks]) =>
						[...references, ...benchmarks].sort(builtInSortFnFor("creationTime", "desc")).map((ptf) => ({
							id: ptf.uuid,
							label: ptf.name ?? "",
							href: ptf.benchmark
								? typedUrlForRoute("LegacyBenchmark", { uuid: ptf.uuid ?? "" })
								: typedUrlForRoute("ReferenceDetails", { uuid: ptf.uuid ?? "" }),
							icon: (
								<ColoredRectangle
									variant="vertical"
									color={ptf.status ? iconAndColorByInvestmentStatus[ptf.status].color : "transparent"}
								/>
							),
						})),
					),
				Universes: () =>
					universeRepository.listBaseAndGroup.fetchQuery().then((universes) =>
						universes.map((universe) => ({
							id: universe.uuid,
							label: universe.name ?? "",
							href:
								universe.universeType === "GROUP_UNIVERSE"
									? typedUrlForRoute("UniverseWithGroupsDetails", { universeUuid: universe.uuid ?? "" })
									: typedUrlForRoute("UniverseDetails", { universeUuid: universe.uuid ?? "" }),
							icon: (
								<ColoredRectangle
									variant="vertical"
									color={universe.status ? iconAndColorByInvestmentStatus[universe.status].color : "transparent"}
								/>
							),
						})),
					),
			},
		),
		// path: async (workspaceUuid?: string) => {
		// 	return workspacesTree.data
		// 		? [
		// 				...(findPathInTree(workspacesTree.data, (node) => node.smartWorkspaceUuid === workspaceUuid, "children")
		// 					?.map((node) => {
		// 						return {
		// 							label: node.name,
		// 							href:
		// 								node.smartWorkspaceUuid &&
		// 								typedUrlForRoute("PortfolioStudio/Workspace", { id: node.smartWorkspaceUuid }),
		// 							nested: !node.smartWorkspaceUuid
		// 								? node.children.map((child) => {
		// 										return {
		// 											label: child.name,
		// 											href:
		// 												child.smartWorkspaceUuid &&
		// 												typedUrlForRoute("PortfolioStudio/Workspace", { id: child.smartWorkspaceUuid }),
		// 										};
		// 									})
		// 								: getWorkspaceEntries(node.smartWorkspaceUuid),
		// 							// : !portfoliosAsNestedCrumbs.data
		// 							// 	? []
		// 							// 	: portfoliosAsNestedCrumbs.data.map((child) => {
		// 							// 			if (child.href === typedUrlForRoute("PortfolioDetails", { portfolioUid: child.id! })) {
		// 							// 				return {
		// 							// 					...child,
		// 							// 					href: typedUrlForRoute("PortfolioDetailsWithWorkspace", {
		// 							// 						workspaceUid: workspaceUuid!,
		// 							// 						portfolioUid: child.id!,
		// 							// 					}),
		// 							// 				};
		// 							// 			}
		// 							// 			return child;
		// 							// 		}),
		// 						};
		// 					})
		// 					.slice(1) ?? []),
		// 			]
		// 		: [];
		// },
		useWorkspacePath: (workspaceUuid?: string) => {
			// biome-ignore lint/correctness/useHookAtTopLevel: nested function is a hook itself will be used
			const latestNestedCrumbs = workspaceRepository.getWorkspaceNestedCrumbs.useQuery(workspaceUuid)?.data;
			// biome-ignore lint/correctness/useHookAtTopLevel: nested function is a hook itself will be used
			const workspaceTree = workspaceRepository.tree.useQuery();
			const workspaceNode = workspaceTree.data
				? findPathInTree(workspaceTree.data, (node) => node.smartWorkspaceUuid === workspaceUuid, "children")
				: undefined;

			if (!workspaceNode || workspaceNode.at(workspaceNode.length - 1)?.name === "Portfolios") {
				return [];
			}

			return workspaceNode
				.map((node): Crumb => {
					if (!node.smartWorkspaceUuid) {
						return {
							label: node.name,
							selectedNestedId: workspaceUuid,
							nested: node.children.map((child) => {
								return {
									id: child.smartWorkspaceUuid,
									label: child.name,
									href:
										child.smartWorkspaceUuid &&
										typedUrlForRoute("PortfolioStudio/Workspace", { id: child.smartWorkspaceUuid }),
								};
							}),
						};
					}

					return {
						label: node.name,
						href: typedUrlForRoute("PortfolioStudio/Workspace", { id: node.smartWorkspaceUuid }),
						nested: latestNestedCrumbs?.map((nestedCrumb) => {
							return {
								label: nestedCrumb.label,
								id: nestedCrumb.id,
								href: nestedCrumb.href,
								icon: (
									<ColoredRectangle
										variant="vertical"
										color={nestedCrumb.status ? iconAndColorByInvestmentStatus[nestedCrumb.status].color : "yellow"}
									/>
								),
							};
						}),
					};
				})
				.slice(1);
		},
		portfolioName: (params?: { name?: string; uuid?: string; workspaceUuid?: string }) => [
			{
				label: params?.name ?? "...",
				href: params?.uuid
					? params.workspaceUuid
						? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
								workspaceUid: params.workspaceUuid,
								portfolioUid: params.uuid,
							})
						: typedUrlForRoute("PortfolioDetails", { portfolioUid: params.uuid })
					: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Portfolios }),
			},
		],
		newPortfolio: [
			{
				label: "New portfolio",
			},
		],
		editPortfolio: [
			{
				label: "Edit portfolio composition",
			},
		],
		reviewInstrument: [
			{
				label: "Review instruments",
			},
		],
		editPortfolioSettings: [
			{
				label: "Portfolio settings",
			},
		],
		enhancePortfolio: (portfolio?: { uuid?: string; name?: string; proposalDate?: string; workspaceUuid?: string }) => [
			{
				label: portfolio?.name ?? "...",
				href: portfolio?.workspaceUuid
					? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
							workspaceUid: portfolio?.workspaceUuid,
							portfolioUid: portfolio?.uuid ?? "",
						})
					: typedUrlForRoute("PortfolioDetails", { portfolioUid: portfolio?.uuid ?? "" }),
			},
			{
				label: `Proposal ${portfolio?.proposalDate ? formatDate(portfolio?.proposalDate) : ""}`,
			},
		],
		createPortfolio: [
			{
				label: "New Portfolio",
			},
		],
		currentPortfolioSettings: (portfolio: { uuid?: string; name?: string; workspaceUuid?: string }) => [
			{
				label: portfolio.name ?? "...",
				href: portfolio.workspaceUuid
					? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
							workspaceUid: portfolio.workspaceUuid,
							portfolioUid: portfolio.uuid!,
						})
					: typedUrlForRoute("PortfolioDetails", { portfolioUid: portfolio.uuid! }),
			},
			{
				label: "Portfolio settings",
			},
		],
		enhancedPortfolioSettings: (portfolio: { uuid?: string; name?: string; workspaceUuid?: string }) => [
			{
				label: portfolio.name ?? "...",
				href: portfolio.workspaceUuid
					? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
							workspaceUid: portfolio.workspaceUuid,
							portfolioUid: portfolio.uuid!,
							proposal: "true",
						})
					: typedUrlForRoute("PortfolioDetails", { portfolioUid: portfolio.uuid!, proposal: "true" }),
			},
			{
				label: "Portfolio settings",
			},
		],
		draftPortfolioSettings: (name?: string) => [
			{
				label: name ?? "",
			},
		],
		//#endregion Portfolio

		//#region Reference
		newTargetPortfolio: [
			{
				label: "New Reference",
			},
		],
		benchmarkName: (params: { name?: string; uuid?: string }) => [
			{
				label: params?.name ?? "...",
				href: params?.uuid
					? typedUrlForRoute("LegacyBenchmark", { uuid: params.uuid })
					: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
			},
		],
		editBenchmark: [
			{
				label: "Edit benchmark composition",
			},
		],
		referenceName: (params?: { name?: string; uuid?: string }) => [
			{
				label: params?.name ?? "...",
				href: params?.uuid
					? typedUrlForRoute("ReferenceDetails", { uuid: params.uuid })
					: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
			},
		],
		editReference: [
			{
				label: "Edit reference composition",
			},
		],
		//#endregion Reference

		//#region Universe
		universeName: (params: { name?: string; uuid?: string }) => [
			{
				label: params?.name ?? "...",
				href: params?.uuid
					? typedUrlForRoute("UniverseDetails", { universeUuid: params?.uuid })
					: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
			},
		],
		newUniverse: [
			{
				label: "New universe",
			},
		],
		newUniverseWithGroups: [
			{
				label: "New universe with groups",
			},
		],
		editUniverse: [
			{
				label: "Edit universe",
			},
		],
		//#endregion Universe

		//#region Market view
		marketViewType(params: { marketViewType?: MarketViewType }) {
			return [
				{
					label:
						params.marketViewType === "EXPECTED_RETURNS_VOLATILITY"
							? t("EXPECTED_RETURNS_VOLATILITY")
							: t("POSITIONING_INDICATORS"),
				},
			];
		},
		marketViewName(params: { name?: string; uuid?: string }) {
			return [
				{
					label: ellipsis(params?.name ?? "...", 20),
					href: params?.uuid
						? typedUrlForRoute("MarketViewWorkSpace/View", { uuid: params.uuid })
						: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.MarketViews }),
				},
			];
		},
		duplicateMarketView: (originalMarketView: { uuid?: string; name?: string }) => [
			{
				label: ellipsis(originalMarketView?.name ?? "Untitled", 20),
				href: typedUrlForRoute("MarketViewWorkSpace/View", {
					uuid: originalMarketView?.uuid ?? "",
				}),
			},
			{
				label: "Duplicate",
			},
		],
		//#endregion Market view

		//#region Market insights
		marketInsights: (() => {
			const regions = typedObjectValues(HmmRegion)
				.filter((region) => region !== "GLOBAL" || hasAccess(user, { requiredService: "GLOBAL_RISK_MAP" }))
				.map((region) => ({
					id: region,
					label: region,
					href: typedUrlForRoute("RegimeAnalysisTool", { region }),
				}));
			const base = fromAppDrawerModules(
				{
					icon: "market-insight" satisfies IconName,
				},
				marketInsight,
				{
					"Regime analysis tool": regions,
				},
			);

			return (...[area, thirdLevelSelectedNestedId]: Parameters<typeof base>) => [
				...base(area, thirdLevelSelectedNestedId),
				...(area === "Regime analysis tool" ? (regions.filter((r) => r.id === thirdLevelSelectedNestedId) ?? []) : []),
			];
		})(),
		//#endregion Market insights

		//#region Storyfolio
		storyfolio: fromAppDrawerModules(
			{ icon: "Storyfolio" satisfies IconName, href: typedUrlForRoute("Storyfolio/Studio", {}) },
			storyfolio,
			{
				"Commentary builder": () =>
					storyfolioRepository.templates.fetchQuery().then((list) =>
						list.map((commentary) => ({
							id: commentary.uuid,
							label: commentary.name ?? "",
							href: typedUrlForRoute("Storyfolio/Details", { uuid: commentary.uuid }),
						})),
					),
				"Report builder": () =>
					reportRepository.templates.fetchQuery().then((list) =>
						list.map((report) => ({
							id: (report?.uuid ?? report?.id)!,
							label: report.templateName ?? "",
							href: typedUrlForRoute("PortfolioStudioSettings/ReportEditor", {
								reportUid: (report?.uuid ?? report?.id)!,
							}),
						})),
					),
			},
		),
		reportName: (template?: { uuid?: string; id?: string; name?: string }) => [
			{
				label: template?.name,
				href: typedUrlForRoute("PortfolioStudioSettings/ReportEditor", {
					reportUid: template?.uuid ?? template?.id ?? "",
				}),
			},
		],
		commentaryName: (commentary?: { uuid?: string; name?: string }) => [
			{
				label: commentary?.name,
				href: typedUrlForRoute("Storyfolio/Details", { uuid: commentary?.uuid ?? "" }),
			},
		],
		//#endregion Storyfolio

		//#region My settings
		mySettings: fromAppDrawerModules(
			{
				href: typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {}),
				icon: "Settings" satisfies IconName,
			},
			settings,
			{},
		),
		//#endregion My settings

		//#region Admin panel
		adminPanel: fromAppDrawerModules({ icon: "Optimize" satisfies IconName }, admin, {}),
		//#endregion Admin panel
	} satisfies Record<string, Array<Crumb> | ((...args: any[]) => Array<Crumb>)>;
};

type GeneralAppDrawerModuleItem<Name extends string> = AppDrawerModuleItem<Name> | AppDrawerTreeNode<Name>;

function fromAppDrawerModules<SecondLevelModule extends GeneralAppDrawerModuleItem<string>>(
	firstLevelCrumb: Crumb & { nested?: never },
	secondLevels: SecondLevelModule[],
	thirdLevelsBySecondLevel: Partial<
		Record<
			SecondLevelModule extends GeneralAppDrawerModuleItem<infer Name> ? Name : string,
			NestedCrumb[] | AsyncState<NestedCrumb[]> | (() => Promise<NestedCrumb[]>)
		>
	>,
): (
	selectedSecondLevelName: SecondLevelModule extends GeneralAppDrawerModuleItem<infer Name> ? Name : string,
	selectedThirdLevelName?: string,
) => Array<Crumb> {
	return (selectedSecondLevelName, selectedThirdLevelName) => {
		const secondLevel = secondLevels.find((x) => x.name === selectedSecondLevelName);
		const secondLevelCrumb =
			secondLevel && "href" in secondLevel
				? {
						label: secondLevel?.title ?? "-",
						href: secondLevel.href,
						selectedNestedId: selectedThirdLevelName,
						nested: thirdLevelsBySecondLevel[secondLevel.name as keyof typeof thirdLevelsBySecondLevel],
					}
				: null;

		const firstLevelCrumbWithNested: Crumb = {
			...(firstLevelCrumb as Crumb),
			selectedNestedId: selectedSecondLevelName,
			nested: secondLevels
				.filter((x) => "href" in x)
				.map((second) => ({
					id: second.name,
					label: second.title,
					href: second.href as string,
				})),
		};

		if (!secondLevelCrumb) {
			return [firstLevelCrumbWithNested];
		}

		return [firstLevelCrumbWithNested, secondLevelCrumb];
	};
}
